import { ImmutableMap } from "@suse-o11y/immutable-map";

import type { StreamOutput, StreamOutputLoading } from "./output";

export const STREAM_OUTPUT_LOADING: StreamOutputLoading = {
	data: undefined,
	error: null,
	isLoading: true,
	isError: false,
	isSuccessful: false,
};

/**
 * The StreamStore is a simple store that holds the stream outputs.
 * The key value is a stream configuration, so different configurations for the same stream type can be stored.
 */
export class StreamStore<TManager> {
	/**
	 * Initializes the StreamStore with the StreamManager schema.
	 */
	public static empty<TManager>(manager: TManager): StreamStore<TManager> {
		return new StreamStore(manager, ImmutableMap.empty());
	}

	private constructor(
		/**
		 * The manager parameter is never used but it holds the StreamManager type info.
		 * So it makes sure that the store is always created via the StreamManger#manage() method.
		 */
		protected readonly manager: TManager,
		private readonly state: ImmutableMap<string, StreamOutput<unknown, unknown>>,
	) {}

	/**
	 * Returns the stream output by the key.
	 */
	public get(key: string): StreamOutput<unknown, unknown> {
		return this.state.get(key) ?? STREAM_OUTPUT_LOADING;
	}

	/**
	 * Updates the stream output by the key.
	 */
	public update(key: string, update: (current: [unknown, unknown]) => [unknown, unknown]): StreamStore<TManager> {
		const output = this.state.update(key, (current = STREAM_OUTPUT_LOADING) => {
			const [error, data] = update([current.error, current.data]);

			if (error == null) {
				return {
					data,
					error: null,
					isLoading: false,
					isError: false,
					isSuccessful: true,
				};
			}

			return {
				data,
				error,
				isLoading: false,
				isError: true,
				isSuccessful: false,
			};
		});

		return new StreamStore(this.manager, output);
	}
}
